import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, RefreshCw, WifiOff, FileDown } from "lucide-react";
import {
  Target,
  Lightbulb,
  BookOpen,
  Code2,
  Brain,
  Rocket,
  Trophy,
  Sparkles,
} from "lucide-react";
import Layout from "../layouts/Layout";
import BackIcon from "../assets/course-details/left-arrow.png";
import PriceTag from "../assets/course-details/price-tag.svg";
import Button from "../components/common/Button";
import Toast from "../components/common/Toast";
import { BtnIcon, Heart } from "../components/Icons";
import Book from "../assets/course-details/book.svg";
import LearnItem from "../components/LearnItem";
import ModuleCard from "../components/cards/ModuleCard";
import ModuleIcon from "../assets/course-details/module-icon.svg";
import CTASection from "../components/CTASection";
import FAQ from "../components/FAQ";
import Thumbnail from "../assets/course-details/what-you-learn-thumbnail.png";
import {
  useGetCourseFeedbacksQuery,
  useGetPublicCourseByIdQuery,
} from "../services/course";
import PricingCard from "../components/cards/PricingCard";
import StudentsReviews from "../components/home/StudentsReviews";
const learningIcons = [
  Target,
  Lightbulb,
  BookOpen,
  Code2,
  Brain,
  Rocket,
  Trophy,
  Sparkles,
];
const SectionPlaceholder = ({
  type = "empty",
  title,
  description,
  onRetry,
  className = "",
}) => (
  <div
    className={`flex flex-col items-center justify-center gap-3 rounded-[18px] border border-dashed border-[#D1D5DB] bg-[#FAFAFA] px-6 py-10 text-center ${className}`}
  >
    {type === "loading" && (
      <>
        <Loader2 className="h-8 w-8 animate-spin text-[#FF6636]" />
        <p className="text-base font-medium text-[#4B5563]">
          {title || "Loading..."}
        </p>
      </>
    )}
    {type === "error" && (
      <>
        <WifiOff className="h-8 w-8 text-[#FF6636]" />
        <p className="text-base font-semibold text-[#1F2937]">
          {title || "Connection problem"}
        </p>
        <p className="max-w-sm text-sm leading-5 text-[#6B7280]">
          {description ||
            "Please check your internet connection and try again."}
        </p>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="mt-1 flex items-center gap-2 text-sm font-semibold text-[#FF6636] hover:underline"
          >
            <RefreshCw size={14} />
            Try again
          </button>
        )}
      </>
    )}
    {type === "empty" && (
      <>
        <p className="text-base font-semibold text-[#4B5563]">
          {title || "No data available"}
        </p>
        <p className="max-w-sm text-sm leading-5 text-[#9CA3AF]">
          {description || "Content for this section is not available yet."}
        </p>
      </>
    )}
  </div>
);

const getCourseOutlineUrl = (outline) => {
  if (!outline) return null;
  if (typeof outline === "string") return outline;
  return outline.secureUrl || outline.url || null;
};

const CourseDetails = () => {
  const { courseId } = useParams();
  const [toast, setToast] = useState({
    show: false,
    title: "",
    message: "",
  });
  const {
    data: publicCourseResponse,
    isLoading,
    isError,
    refetch,
  } = useGetPublicCourseByIdQuery(courseId, {
    skip: !courseId,
  });

  const publicCourse = publicCourseResponse?.data;
  const course = publicCourse?.course;
  const courseOverview = course?.courseOverview || {};

  const pageDescription = courseOverview?.courseDescription;
  const heroImageSrc = course?.courseThumbnail?.secureUrl;
  const courseOutlineUrl = getCourseOutlineUrl(course?.courseOutline);

  const apiModules = publicCourse?.curriculum;
  const modules = Array.isArray(apiModules)
    ? apiModules.map((item, idx) => ({
        id: idx + 1,
        title: item.moduleName,
        description: item.moduleDescription,
        icon: ModuleIcon,
      }))
    : [];

  const handleDownloadOutline = () => {
    if (!courseOutlineUrl) return;
    window.open(courseOutlineUrl, "_blank", "noopener,noreferrer");
  };

  const apiLearnSource = publicCourse?.learningOutcomes;
  const learnData = Array.isArray(apiLearnSource)
    ? apiLearnSource.map((item, index) => ({
        id: item._id || index,
        icon: learningIcons[index % learningIcons.length],
        description: item.outcomeDescription,
      }))
    : [];

  const { data: courseFeedbacksResponse } =
    useGetCourseFeedbacksQuery(courseId);
  const courseFeedbacks = courseFeedbacksResponse?.data;

  return (
    <Layout>
      <section className="border-t border-t-[#938A9636] bg-[#F5F5F5] px-5 sm:px-8 pb-12 sm:pb-16 lg:pb-24">
        <div className="mx-auto mt-[2px] w-full max-w-[1754px] bg-white  border shadow-[0_0_4px_2px_#00000040]  pt-8 sm:pt-12 lg:pt-16 xl:pt-[77px]">
          <div className="flex flex-col  lg:flex-row justify-between gap-10 px-6 sm:px-10 md:px-14 xl:pl-[72px] xl:pr-[99px]">
            <div className="w-full max-w-[740px]">
              <Link to="/courses" className="ml-[2px] inline-block">
                <img src={BackIcon} alt="Back" width={21} height={21} />
              </Link>
              <h1 className="mt-4 font-bold leading-9  sm:leading-[44px] tracking-[-1%] sm:mt-6 text-3xl sm:text-4xl 2xl:text-[40px] 2xl:leading-[56px] ">
                Kickstart Your {course?.courseTitle || ""} Journey with{" "}
                <span className="text-[#FF6636]">
                  Beginner-Friendly Courses for Teens
                </span>
              </h1>
              <p className="mt-4 max-w-[740px] leading-[19px]">
                {pageDescription}
              </p>
              <div className="mt-6   sm:mt-11">
                <div className="flex items-center gap-1">
                  <img
                    src={PriceTag}
                    alt="icon"
                    style={{ width: 20, height: 20 }}
                  />
                  <p className="text-base font-medium leading-[21px] sm:text-lg">
                    {course?.coursePrice || "$99.9"}
                  </p>
                </div>
              </div>

              <div className="mt-6 flex w-full flex-col gap-3 sm:mt-8">
                <div className="flex w-full flex-col gap-3 sm:flex-row sm:flex-wrap sm:gap-[10px] [&>div]:w-full sm:[&>div]:min-w-0 sm:[&>div]:flex-1 lg:[&>div]:flex-none lg:[&>div]:w-auto">
                  <Button
                    icon={BtnIcon}
                    btnText="Book a Free Demo"
                    onClick={() =>
                      setToast({
                        show: true,
                        title: "Login Required",
                        message:
                          "Create an account or log in to schedule your free demo session.",
                      })
                    }
                    btnClassName={
                      "w-full bg-[#FF6636] hover:bg-opacity-15 hover:text-[#FF6636] flex-row-reverse gap-3 px-6 py-[14px] text-base font-semibold text-white lg:px-9 lg:text-lg"
                    }
                  />
                  <Button
                    icon={Heart}
                    btnText="Add to Wishlist"
                    onClick={() =>
                      setToast({
                        show: true,
                        title: "Login Required",
                        message:
                          "Create an account or log in to wishlist the course",
                      })
                    }
                    btnClassName={
                      "h-full w-full gap-4 border border-black px-6 py-[14px] text-sm font-bold text-black lg:gap-6 lg:px-9"
                    }
                  />
                </div>

                {courseOutlineUrl ? (
                  <Button
                    icon={FileDown}
                    btnText="Download Course Outline"
                    onClick={handleDownloadOutline}
                    btnClassName={
                      "w-full gap-3 border border-[#923D8C] px-6 py-[14px] text-sm font-bold text-[#923D8C] hover:bg-[#923D8C0D] sm:w-fit lg:px-9"
                    }
                  />
                ) : null}
              </div>
            </div>

            <div className="hidden lg:block relative mx-auto mt-0 w-full max-w-[590px]  xl:max-w-[659px]  lg:mx-0 lg:mt-10">
              <div className="relative aspect-[590/400] xl:aspect-[659/480] w-full">
                {isLoading ? (
                  <SectionPlaceholder
                    type="loading"
                    title="Loading course image..."
                    className="h-full w-full"
                  />
                ) : isError ? (
                  <SectionPlaceholder
                    type="error"
                    title="Couldn't load course image"
                    description="We couldn't reach the server. Check your connection and try again."
                    onRetry={refetch}
                    className="h-full w-full"
                  />
                ) : heroImageSrc ? (
                  <>
                    <img
                      src={heroImageSrc}
                      alt="hero"
                      className="relative z-10 h-full w-full object-cover"
                    />
                    <div className="pointer-events-none absolute   h-full w-full border-2 border-[#923D8C] shadow-[0_0_4px_0px_#00000040] -top-4 left-3 xl:-top-[30px] xl:left-[26px]" />
                  </>
                ) : (
                  <SectionPlaceholder
                    type="empty"
                    title="No course image available"
                    description="The image for this course hasn't been added yet."
                    className="h-full w-full"
                  />
                )}
              </div>
            </div>
          </div>

          <div className="mt-14 sm:mt-10 2xl:-mt-1 flex flex-col gap-3 sm:gap-4  sm:flex-row sm:items-center px-6 sm:px-10 md:px-14 xl:pl-[69px]">
            <div className="shrink-0">
              <img src={Book} alt="book icon" />
            </div>
            <div>
              <h3 className="leading-tight tracking-normal text-3xl lg:text-[32px] lg:leading-[56px]">
                What you’ll Learn
              </h3>
              <p className="text-base leading-5 sm:text-lg 2xl:max-w-max max-w-[588px] ">
                Gain the skills that top companies are looking for in modern web
                developers
              </p>
            </div>
          </div>

          <div className=" flex flex-col   xl:flex-row px-6 sm:px-10 md:px-14 xl:px-[69px] justify-between gap-10 xl:pr-24 pt-5  2xl:pt-0">
            <div className="w-full space-y-5  2xl:mt-9">
              {isLoading ? (
                <SectionPlaceholder
                  type="loading"
                  title="Loading learning outcomes..."
                />
              ) : isError ? (
                <SectionPlaceholder
                  type="error"
                  title="Couldn't load learning outcomes"
                  description="We couldn't reach the server. Check your connection and try again."
                  onRetry={refetch}
                />
              ) : learnData.length > 0 ? (
                learnData.map((itm, idx) => (
                  <LearnItem
                    key={itm.id}
                    icon={itm.icon}
                    description={itm.description}
                    index={idx}
                  />
                ))
              ) : (
                <SectionPlaceholder
                  type="empty"
                  title="No learning outcomes yet"
                  description="What you'll learn for this course will be shared here soon."
                />
              )}
            </div>
            <div className="w-full   max-w-[567px] shrink-0 flex justify-center mx-auto xl:mx-0 ">
              <PricingCard
                showToast={true}
                livePrice={course?.coursePrice}
                recordedPrice={course?.coursePrice}
              />
            </div>
          </div>

          {/* <div className=" flex w-full flex-col  xl:flex-row justify-between px-[69px]">
            <div className="w-full max-w-[908px] -mt-48 ">
              <h3 className="text-3xl leading-tight tracking-normal sm:text-4xl lg:leading-[56px] lg:text-[40px]">
                Our {course?.courseTitle || ""} Class Curriculum
              </h3>
              <p className="ml-1 mt-4 leading-[19px]">
                {isLoading
                  ? "Loading modules..."
                  : isError
                    ? "Unable to load modules"
                    : `${modules.length} Modules`}
              </p>

              <div className=" mt-8 flex flex-col gap-5 sm:mt-[60px] sm:gap-7">
                {isLoading ? (
                  <SectionPlaceholder
                    type="loading"
                    title="Loading curriculum..."
                  />
                ) : isError ? (
                  <SectionPlaceholder
                    type="error"
                    title="Couldn't load curriculum"
                    description="We couldn't reach the server. Check your connection and try again."
                    onRetry={refetch}
                  />
                ) : modules.length > 0 ? (
                  modules.map((module) => (
                    <ModuleCard key={module.id * 4} module={module} />
                  ))
                ) : (
                  <SectionPlaceholder
                    type="empty"
                    title="No curriculum available yet"
                    description="Class modules and lessons for this course will appear here once added."
                  />
                )}
              </div>
            </div>
            <div className="mt-[61px] relative mx-auto w-full max-w-[567px] shrink-0 xl:mx-0">
              <div className="relative aspect-[550/430]  w-full">
                <img
                  src={Thumbnail}
                  alt="thumbnail"
                  className="absolute right-0 top-2.5 z-10 h-[calc(100%-10px)] w-[calc(100%-28px)] object-cover sm:right-12"
                />
                <div className="pointer-events-none h-[387px] w-[542px] border-2 border-[#FF6636] shadow-[0_0_4px_0px_#00000040]" />
              </div>
            </div>
          </div> */}

          <div className="mb-12 mt-12 px-6 lg:mb-[103px] lg:mt-[107px] lg:px-10">
            <CTASection
              title={`Start Mastering ${course?.courseTitle || "Web Development"} Today`}
              textSize="text-3xl sm:text-4xl lg:text-5xl xl:text-[55px]"
              subText={`Unlock your potential and dive into the world of ${course?.courseTitle || "web development"}
            with hands-on courses designed for all skill levels.`}
              btnText="Buy Now"
              btnSize="w-full  xl:px-[97px]"
              isAddToCartVisible={true}
            />
          </div>
          <StudentsReviews
            padding="pl-6 2xl:pl-[35px]"
            margin="mt-[66px]"
            width="max-w-[1558px]"
            reviews={courseFeedbacks}
          />
          <FAQ />
        </div>
      </section>
      {toast.show && (
        <Toast
          title={toast.title}
          message={toast.message}
          onClose={() => setToast((prev) => ({ ...prev, show: false }))}
        />
      )}
    </Layout>
  );
};

export default CourseDetails;
